import server from "@/utils/server";

const wallet = {
  namespaced: true,
  state: {
    wallets: [],
    hidden: [],
  },
  mutations: {
    setWallets(state, payload) {
      state.wallets = payload;
    },
    setHidden(state, payload) {
      state.hidden = payload;
    },
  },
  getters: {
    network(state, getters, rootState, rootGetters) {
      return rootGetters["network/chainName"].toLowerCase();
    },
    wallets(state) {
      return state.wallets;
    },
    hidden(state) {
      return state.hidden;
    },
    screen(state) {
      return state.wallets.filter(
        (wallet) =>
          !state.hidden.some((hidden) => hidden.address === wallet.address)
      );
    },
    balanceNative: (state) => (address) => {
      const wallet = state.wallets.find((wallet) => wallet.address === address);
      return wallet && wallet.balanceNative !== undefined
        ? wallet.balanceNative
        : 0;
    },
    balanceToken: (state) => (address) => {
      const wallet = state.wallets.find((wallet) => wallet.address === address);
      return wallet && wallet.balanceToken !== undefined
        ? wallet.balanceToken
        : 0;
    },
    totalBalanceNative(state) {
      return state.wallets.reduce((total, wallet) => {
        return total + (wallet.balanceNative || 0);
      }, 0);
    },
    totalBalanceToken(state) {
      return state.wallets.reduce((total, wallet) => {
        return total + (wallet.balanceToken || 0);
      }, 0);
    },
  },
  actions: {
    async init({ getters, commit, dispatch }) {
      commit("setWallets", []);
      commit("setHidden", []);
      let storageData = localStorage.getItem(getters.network);
      if (storageData) {
        storageData = JSON.parse(storageData);
        const hidden = storageData["hidden"] ?? [];
        commit("setHidden", hidden);
      }
      await dispatch("list");
    },
    async list({ rootState, getters, commit }) {
      try {
        const response = await server.post("/wallet/list", {
          token: rootState.auth.token,
          network: getters.network,
        });
        if (response.status === 200) {
          const result = response.data;
          commit("setWallets", JSON.parse(result.data));
        }
        return response.data;
      } catch (e) {
        commit("setWallets", []);
        return false;
      }
    },
    async create({ state, getters, rootGetters, commit, dispatch }) {
      try {
        const wallets = state.wallets;
        let wallet = {
          name: `Wallet ${wallets.length + 1}`,
          address: null,
          privateKey: null,
          balanceNative: 0,
          balanceToken: 0,
        };
        switch (getters.network) {
          case "solana":
            wallet = { ...wallet, ...rootGetters["solana/generateWallet"] };
            break;
          case "ethereum":
          case "base":
          case "bsc":
          default:
            wallet = { ...wallet, ...rootGetters["ethereum/generateWallet"] };
            break;
        }
        wallets.push(wallet);
        commit("setWallets", wallets);
        await dispatch("update");
        commit(
          "toast/add",
          {
            type: "success",
            title: `${wallet.name} has been successfully created!`,
            content: `<a class="text-blue-500 hover:underline" target='_blank' href='${rootGetters[
              "network/shortAddress"
            ](wallet.address)}'>View on ${getters.network} blockchain</a>`,
          },
          { root: true }
        );
      } catch (e) {
        console.log(e);
      }
    },
    async update({ state, rootState, getters }) {
      try {
        await server.post("/wallet/update", {
          token: rootState.auth.token,
          network: getters.network,
          data: JSON.stringify(state.wallets),
        });
      } catch (e) {
        console.log(e);
      }
    },
    async remove({ state, getters, rootGetters, commit, dispatch }, wallet) {
      try {
        const wallets = state.wallets.filter(
          (item) => item.address !== wallet.address
        );
        commit("setWallets", wallets);
        await dispatch("update");
        commit(
          "toast/add",
          {
            type: "success",
            title: `${wallet.name} has been successfully removed! `,
            content: `<a class="text-blue-500 hover:underline" target='_blank' href='${rootGetters[
              "network/shortAddress"
            ](wallet.address)}'>View on ${getters.network} blockchain</a>`,
          },
          { root: true }
        );
      } catch (e) {
        console.log(e);
      }
    },
    async removeAll({ commit, dispatch }) {
      try {
        commit("setWallets", []);
        await dispatch("update");
        commit(
          "toast/add",
          {
            type: "success",
            title: "All wallets have been successfully removed!",
          },
          { root: true }
        );
      } catch (e) {
        console.log(e);
      }
    },
    async import({ state, commit, dispatch }, importWallets) {
      try {
        importWallets = importWallets.filter(
          (wallet) =>
            !state.wallets.some((item) => item.address === wallet.address)
        );
        const totalWallets = state.wallets.length;
        importWallets = importWallets.map((wallet, index) => ({
          name: `Wallet ${totalWallets + index + 1}`,
          ...wallet,
          balanceNative: 0,
          balanceToken: 0,
        }));
        const wallets = [...state.wallets, ...importWallets];
        commit("setWallets", wallets);
        await dispatch("update");
        commit(
          "toast/add",
          {
            type: "success",
            title: `Total ${importWallets.length} wallets have been successfully imported!`,
          },
          { root: true }
        );
      } catch (e) {
        console.log(e);
      }
    },
    hiddenWallet({ state, getters }, payload) {
      if (Array.isArray(payload)) {
        state.hidden = payload;
      } else {
        state.hidden.push(payload);
      }
      let storageData = localStorage.getItem(getters.network);
      if (storageData) {
        storageData = JSON.parse(storageData);
        storageData = Object.assign({}, storageData, { hidden: state.hidden });
      } else {
        storageData = { hidden: state.hidden };
      }
      localStorage.setItem(getters.network, JSON.stringify(storageData));
    },
    async updateName({ state, commit, dispatch }, payload) {
      const wallets = state.wallets;
      const wallet = wallets.find(
        (wallet) => wallet.address === payload.address
      );
      if (wallet) {
        wallet.name = payload.name;
      }
      commit("setWallets", wallets);
      await dispatch("update");
    },
    updateBalanceNative({ state, commit }, payload) {
      const wallets = state.wallets;
      const wallet = wallets.find(
        (wallet) => wallet.address === payload.address
      );
      if (wallet) {
        wallet.balanceNative = payload.balance;
      }
      commit("setWallets", wallets);
    },
    updateBalanceToken({ state, commit }, payload) {
      const wallets = state.wallets;
      const wallet = wallets.find(
        (wallet) => wallet.address === payload.address
      );
      if (wallet) {
        wallet.balanceToken = payload.balance;
      }
      commit("setWallets", wallets);
    },
    refreshBalanceTokenAllWallet({ state, commit }) {
      const wallets = state.wallets;
      wallets.forEach((wallet) => {
        wallet.balanceToken = 0;
      });
      commit("setWallets", wallets);
    },
  },
};

export default wallet;
